import { createPopup, IPopup, IPopupOptions } from "../technical/popup-new";
import { expressEventListener, expressQuerySelector, expressQuerySelectorAll } from "../../common/html";
import { getToken } from "../../services/auth-service";
import { addParameter, getParameter } from "../../utils/querystring";
import {
	getPhotosAsync,
	getPhotoStoreAsync,
	IPhotoStore,
	PartnerPhoto,
	StoreType,
} from "../../services/storageService";
import { connectToAsync } from "../technical/photostoreconnector";
import { Downloader } from "../technical/downloader";
import { showLoadingDialog } from "../popups/loading-dialog";
import { ExternalPhotoPicker } from "../technical/externalphotopicker";
import { compositeDisposable, IDisposable } from "../../utils/disposable";
import { createUpload } from "../technical/upload";
import { createUploadDialog } from "../popups/upload-dialog";
import { getCookie } from "../../utils/cookie";
import { createPageLog, IPageLog, PageLogStyle } from "../molecules/page-log";
export interface IPhotoImport {
	selection: IPhoto[] | null;
	storeType: StoreType;
}

export interface IPhoto {
	readonly code: string;
	readonly name: string;
	readonly description: string;
	readonly originalFilename: string;
	readonly thumbUrl: string;
	readonly mediumUrl: string;
	readonly width: number;
	readonly height: number;
	readonly fileSize: number;
	readonly fileId: string;
	readonly dateTaken?: Date;
	readonly dateUploaded?: Date;
	readonly dateModified?: Date;
	readonly rotate90?: number;
}

export class PhotoSourcesPopup {
	private externalPicker: ExternalPhotoPicker | undefined;
	private pickerWindow: Window | null;
	private selectPhotoSourcePopup: IPopup;
	private connectedSourcePopup: IPopup;
	private maxSelectableItems: number;
	private onImportPhotosFinished: (photos: IPhoto[]) => Promise<void>;
	private photoSourcesEventDisposables: IDisposable = compositeDisposable(undefined);
	private pageLog: IPageLog;
	private imageInvalidLabel: string;

	constructor(onImportPhotosFinished: (photos: IPhoto[]) => Promise<void>, maxSelectableItems = 0) {
		this.onImportPhotosFinished = onImportPhotosFinished;
		this.maxSelectableItems = maxSelectableItems;
	}

	public openPhotoStoresPopup = async (popupOptions: { onBeforeClose: () => void }, errorMessage?: string) => {
		// create and open the popup
		this.selectPhotoSourcePopup = await createPopup({
			dialogId: "PhotoSourcesDialog",
			view: "PhotoSourcesDialog",
			forceRefresh: true,
			onBeforeClose: () => {
				this.closeConnectionAsync();
				this.photoSourcesEventDisposables.dispose();
				popupOptions.onBeforeClose();
			},
		});

		const pageLogEl = expressQuerySelector<HTMLElement>(
			this.selectPhotoSourcePopup.popup,
			".technical-top-message-log",
			false,
		);
		if (pageLogEl) {
			this.pageLog = createPageLog(pageLogEl);
			this.imageInvalidLabel = pageLogEl.parentElement.dataset.imageInvalidLabel;
			errorMessage && this.pageLog.updateMessageAndStyle(PageLogStyle.Error, errorMessage);
		}

		this.initDeviceUpload();
		this.initPhotoSources();

		await this.selectPhotoSourcePopup.openAsync();
	};

	private initDeviceUpload = (): void => {
		const fromDeviceEls = expressQuerySelectorAll<HTMLElement>(document, ".technical-upload-from-device");

		fromDeviceEls.forEach((fromDeviceEl) => {
			expressEventListener(fromDeviceEl, "click", (e) => {
				if (e.target.tagName == "INPUT") return;
				this.startUploadFromDevice(fromDeviceEl);
			});
		});
	};

	private startUploadFromDevice = async (parentEl: HTMLElement): Promise<void> => {
		const fromDeviceEventDisposables: IDisposable = compositeDisposable(undefined);
		const fileEl = expressQuerySelector<HTMLInputElement>(parentEl, 'input[type="file"]', true);

		const processUploadFiles = async (ev: Event) => {
			const fileEl = ev.target as HTMLInputElement;
			const files = fileEl.files;
			if (!files || files.length == 0) return;

			fromDeviceEventDisposables.dispose();

			const uploadDialog = await createUploadDialog(files.length);

			const onUploadFinished = (
				folderId: string,
				importedFileIds: string[] | null,
				failedUploadAmount: number,
				failedFileNames: string[] | null,
			) => {
				if (!importedFileIds || !importedFileIds.length) {
					uploadDialog && uploadDialog.close();
					return;
				}

				getPhotosAsync(window.context.pointOfSaleId, getCookie("accid"), folderId, importedFileIds).then(
					async (smartphotoPhotos) => {
						uploadDialog && uploadDialog.close();
						try {
							await this.onImportPhotosFinished(smartphotoPhotos);
						} catch (error) {
							await this.openPhotoStoresPopup(
								{
									onBeforeClose: () => {
										return;
									},
								},
								error,
							);
						}
					},
				);
			};

			const onFileUploaded = (amountUploadedFiles: number) => {
				uploadDialog && uploadDialog.updateProgress && uploadDialog.updateProgress(amountUploadedFiles);
			};

			const onBeforeUploadStart = () => {
				uploadDialog && uploadDialog.updateProgress && uploadDialog.updateProgress(0);
			};

			const folderId = parentEl.dataset.folderid || "";
			const uploadBatchSize = parseInt(parentEl.dataset.uploadbatchsize || "5", 10);

			// add the uploadDialog and finish methods with settings to the upload component
			await createUpload(files, folderId, {
				onBeforeUploadStart,
				onUploadFinished,
				onFileUploaded,
				batchSize: uploadBatchSize,
				flow: "ai-filter",
			});
		};

		fromDeviceEventDisposables.add(expressEventListener(fileEl, "change", processUploadFiles));

		// open the file-upload
		fileEl.click();
	};

	private initPhotoSources = (): void => {
		const photosourceEls = expressQuerySelectorAll<HTMLElement>(document, ".technical-photosource");
		photosourceEls.forEach((photosourceEl) => {
			this.photoSourcesEventDisposables.add(
				expressEventListener(photosourceEl, "click", async (ev: any) => {
					this.pageLog.hideLog();

					let storeType: StoreType;
					switch (photosourceEl.dataset.storetype.toLowerCase()) {
						case "facebook":
							storeType = StoreType.Facebook;
							break;
						case "googlephotos":
							storeType = StoreType.GooglePhotos;
							break;
						case "local":
							storeType = StoreType.Local;
							break;
						case "swisscom":
							storeType = StoreType.SwissCom;
							break;
						default:
							storeType = StoreType.eXpress;
							break;
					}

					const connectedToStoreAsync = async (connectedStore: IPhotoStore | null) => {
						if (!connectedStore || !connectedStore.IsConnected || !connectedStore.HasAlbumAccess) return;

						if (this.connectedSourcePopup) await this.closeImportFromAsync();

						const connectedTooltip = expressQuerySelector(
							document,
							".technical-photosource-connectedas[data-storetype='" + connectedStore.Code.toLowerCase() + "']",
						);
						const connectedTooltipLabel = expressQuerySelector(
							connectedTooltip,
							".technical-photosource-connectedas-label",
						);
						connectedTooltipLabel.innerHTML = connectedTooltipLabel.innerHTML.replace(
							"{0}",
							connectedStore.ConnectedUserName,
						);
						connectedTooltip.classList.remove("u-hide");

						if (connectedStore.StoreType === StoreType.GooglePhotos && connectedStore.UseExternalPhotoPicker) {
							this.openImportFromGoogleAsync(connectedStore);
							return;
						}

						this.openImportFromPopup(connectedStore);
					};

					const photoStore = await showLoadingDialog(getPhotoStoreAsync(storeType.toString()));
					await connectToAsync(photoStore, connectedToStoreAsync);
				}),
			);
		});
	};

	private openImportFromPopup = async (connectedStore: IPhotoStore) => {
		const fromPartnerEventDisposables: IDisposable = compositeDisposable(undefined);

		const frameEl = document.createElement("iframe");
		frameEl.style.height = "100%;";
		frameEl.style.overflow = "hidden";
		frameEl.style.width = "100%";
		frameEl.style.height = "90vh";
		frameEl.style.border = "0";
		let url = connectedStore.ImportPhotosUrl;
		url = addParameter("maxSelectableItems", this.maxSelectableItems.toString(), url);
		frameEl.src = url;

		// create content-div
		const corePopupContent = document.createElement("div");
		corePopupContent.classList.add("m-popup__body");
		corePopupContent.style.width = "100%";
		corePopupContent.style.padding = "0";
		corePopupContent.append(frameEl);

		// create container-div
		const corePopup = document.createElement("div");
		corePopup.classList.add("m-popup__content");
		corePopup.classList.add("technical-popup-content");
		corePopup.style.display = "flex";
		corePopup.append(corePopupContent);

		const options: IPopupOptions = {
			dialogId: "importphotopopup_" + connectedStore.StoreType,
			view: "GenericPopup",
			forceRefresh: true,
			onBeforeClose: (popupEl: HTMLElement) => {
				fromPartnerEventDisposables.dispose();
			},
			data: {
				BodyText: "",
			},
		};

		this.connectedSourcePopup = await createPopup(options);
		this.connectedSourcePopup.replaceContent(corePopup);

		// add listener
		fromPartnerEventDisposables.add(expressEventListener(document, "importPhotosFinished", this.importPhotosFinished));
		fromPartnerEventDisposables.add(expressEventListener(window, "message", this.messageEvent));

		// close the photosources-popup
		this.selectPhotoSourcePopup.close();

		// open the connected source popup
		await this.connectedSourcePopup.openAsync();
	};

	private messageEvent = async (e) => {
		if (!e.data.command || !e.data || !e.data.command) return;

		if (e.data.command === "CloseImportPhotosPopUp") await this.closeImportFromAsync();
	};

	private importPhotosFinished = async (e) => {
		const data = (e as any).detail as IPhotoImport | null;

		if (data) {
			const convertPhotos = async (data: IPhotoImport): Promise<void> => {
				const smartphotoPhotos = await this.convertToSmartphotoPhotosAsync(data);

				try {
					await this.onImportPhotosFinished(smartphotoPhotos);
				} catch (error) {
					await this.openPhotoStoresPopup(
						{
							onBeforeClose: () => {
								return;
							},
						},
						error,
					);
				}
			};
			await showLoadingDialog(convertPhotos(data));
		}

		await this.closeImportFromAsync();
	};

	public closeImportFromAsync = async () => {
		// remove listeners
		window.removeEventListener("message", this.messageEvent);
		document.removeEventListener("importPhotosFinished", this.importPhotosFinished);

		// close connection
		await this.closeConnectionAsync();
		this.connectedSourcePopup.close();
	};

	private showPopupBlockedBanner = (store: IPhotoStore) => {
		const bannerEl = document.querySelector<HTMLElement>(".technical-mediapartner-connected-popupblocked");
		if (!bannerEl) return;

		bannerEl.innerHTML = bannerEl.innerHTML.replace("{0}", store.Name);
		bannerEl.classList.remove("u-hide");
	};

	private openImportFromGoogleAsync = async (connectedSource: IPhotoStore) => {
		// reuse existing picker
		if (!this.externalPicker) this.externalPicker = new ExternalPhotoPicker();

		await showLoadingDialog(this.externalPicker.createExternalPhotoPicker(this.maxSelectableItems));

		// a window is already open? Try to close it
		if (this.pickerWindow) this.pickerWindow.close();

		// open the picker
		this.pickerWindow = this.externalPicker.openPicker();
		if (this.pickerWindow == null) {
			// We couldn't open the popup => show the banner
			// The customer could open the popup via the browser, so we can't stop here
			// and have to start polling the session
			this.showPopupBlockedBanner(connectedSource);
		}

		// wait for feedback
		const userHasSelectedPhotos = await this.externalPicker.startPickerPolling();
		if (!userHasSelectedPhotos) return;

		const selectPhotosAndCloseConnection = async (externalPicker: ExternalPhotoPicker): Promise<PartnerPhoto[]> => {
			const photos = await externalPicker.getSelectedPhotos();
			await this.closeConnectionAsync();
			return photos;
		};

		const photos = await showLoadingDialog(selectPhotosAndCloseConnection(this.externalPicker));

		const convertPhotos = async (): Promise<void> => {
			const data = { selection: photos, storeType: StoreType.GooglePhotos } as IPhotoImport | null;
			const smartphotoPhotos = await this.convertToSmartphotoPhotosAsync(data);

			// no photos selected? Show a message and return
			if (!smartphotoPhotos) {
				if (this.pageLog && this.imageInvalidLabel) {
					this.pageLog.updateMessageAndStyle(PageLogStyle.Error, this.imageInvalidLabel);
				}
				return null;
			}

			this.selectPhotoSourcePopup.close(); //Close the photo-sources-popup when the user has made his selection
			try {
				await this.onImportPhotosFinished(smartphotoPhotos);
			} catch (error) {
				await this.openPhotoStoresPopup(
					{
						onBeforeClose: () => {
							return;
						},
					},
					error,
				);
			}
		};

		await showLoadingDialog(convertPhotos());
	};

	private convertToSmartphotoPhotosAsync = async (data: IPhotoImport): Promise<IPhoto[]> => {
		const photos = data.selection;
		if (!photos || photos.length == 0) return Promise.resolve(undefined);

		// photos already eXpress? => no need to convert, just return them
		if (data.storeType == StoreType.eXpress) return Promise.resolve(photos);

		const token = await getToken();
		const productCategoryGroupCode = (getParameter("pcg", undefined, false) || "").toLowerCase();
		const downloader = new Downloader("ai-filter", token, productCategoryGroupCode);
		return await downloader.addToQueueAsync(photos as PartnerPhoto[]);
	};

	private closeConnectionAsync = async () => {
		if (this.externalPicker) {
			await this.externalPicker.deleteExternalPhotoPicker();
			this.externalPicker = undefined;
		}

		this.pickerWindow && this.pickerWindow.close();
		this.pickerWindow = null;
	};
}
